import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Star, Clock, Heart } from "lucide-react";
import useApi from "../../../hooks/useApi";
import { API_METHODS } from "../../../constants";
import { isEmpty } from "../../../generalFunctions";
import ServerDownResult from "../../../components/common/ServerDownResult";
import { Button, Spin, message } from "antd";
import Footer from "./Footer";
import HeaderComponent from "./HeaderComponent";
import { WhatsAppCommunityCard, WhatsAppCommunityPill } from "../WhatsappCommunity";
import { useNavigate } from "react-router-dom";

const ServiceCard = React.memo(
  ({
    service,
    customPrices,
    onCustomPriceChange,
    likedServices,
    toggleLike,
    formatCurrency,
    getServiceIcon,
    renderPriceSection,
    onBookNow,
  }) => {
    const [showAllDates, setShowAllDates] = useState(false);
    const isSpecialOccasion =
      service.is_date_restricted && service.service_type_id === 5;
    const availableDates = service.available_dates || [];

    // Determine how many dates to show
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    const maxDatesShown = isMobile ? 3 : 4;
    const visibleDates = availableDates.slice(0, maxDatesShown);
    const hiddenDatesCount = Math.max(0, availableDates.length - maxDatesShown);

    // Format date for display
    const formatDateForDisplay = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      });
    };

    // Handle close modal/bottom sheet
    const handleCloseDates = () => {
      setShowAllDates(false);
    };

    // Handle backdrop click for mobile bottom sheet
    const handleBackdropClick = (e) => {
      if (e.target === e.currentTarget) {
        handleCloseDates();
      }
    };

    return (
      <>
        <div
          className={`bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden group transform hover:-translate-y-2 relative flex flex-col ${
            isSpecialOccasion ? "special-occasion-card" : ""
          }`}
        >
          {/* Special Occasion Gradient Border */}
          {isSpecialOccasion && (
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 p-0.5 animate-gradient-x">
              <div className="h-full w-full bg-white rounded-xl"></div>
            </div>
          )}

          {/* Hero Image Section */}
          <div className="relative h-48 sm:h-56 md:h-48 lg:h-52 overflow-hidden rounded-t-xl z-10">
            {/* Image or Icon Background */}
            <div className="w-full h-full bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center">
              {service.image_url ? (
                <img
                  src={service.image_url}
                  alt={service.name}
                  className="w-full h-full object-cover sm:object-contain md:object-cover group-hover:scale-110 transition-transform duration-500"
                />
              ) : (
                <div className="text-6xl sm:text-7xl md:text-8xl transform group-hover:scale-110 transition-transform duration-300 text-orange-400">
                  {getServiceIcon(service.image_url, service.service_type_icon)}
                </div>
              )}
            </div>

            {/* Gradient Overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>

            {/* Service Badges - Positioned over image */}
            <div className="absolute top-0 left-0 right-0 flex justify-between items-start p-3 z-20">
              <div className="flex flex-col space-y-2">
                {/* Special Occasion Badge */}
                {isSpecialOccasion && (
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 text-xs sm:text-sm font-bold rounded-full shadow-lg animate-pulse">
                    🎉 Special Occasion
                  </div>
                )}
          
                {!service.is_available && (
                  <div className="bg-red-500 text-white px-3 py-1 text-xs sm:text-sm font-medium rounded-full shadow-lg">
                    Currently Unavailable
                  </div>
                )}
              </div>

              {service.price_type === "custom" && (
                <div className="bg-blue-500 text-white px-3 py-1 text-xs sm:text-sm font-medium rounded-full shadow-lg">
                  Custom Amount
                </div>
              )}
            </div>
          </div>

          {/* Card Content */}
          <div className="p-4 sm:p-5 md:p-6 relative z-10 bg-white rounded-b-xl flex flex-col" style={{ flex: 1 }}>
            {/* Service Title */}
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 group-hover:text-orange-600 transition-colors">
              {service.name}
            </h3>

            {/* Service Description */}
            <p className="text-gray-600 mb-4 text-sm leading-relaxed">
              {service.description}
            </p>

            {/* Special Occasion Dates Section */}
            {isSpecialOccasion && availableDates.length > 0 && (
              <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="text-amber-600 font-semibold text-sm">
                    📅 Special Dates:
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 items-center">
                  {visibleDates.map((date, idx) => (
                    <span
                      key={idx}
                      className="inline-block bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium border border-amber-200"
                    >
                      {formatDateForDisplay(date)}
                    </span>
                  ))}
                  {hiddenDatesCount > 0 && (
                    <button
                      onClick={() => setShowAllDates(true)}
                      className="inline-block bg-amber-200 hover:bg-amber-300 text-amber-900 px-2 py-1 rounded-full text-xs font-medium border border-amber-300 transition-colors cursor-pointer"
                    >
                      +{hiddenDatesCount} more
                    </button>
                  )}
                </div>
                {hiddenDatesCount > 0 && (
                  <button
                    onClick={() => setShowAllDates(true)}
                    className="mt-2 text-xs text-amber-700 hover:text-amber-900 font-medium underline"
                  >
                    View All {availableDates.length} Dates →
                  </button>
                )}
              </div>
            )}

            {/* Benefits */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-1">
                {service.benefits.map((benefit, idx) => (
                  <span
                    key={idx}
                    className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded-full border border-orange-200"
                  >
                    {benefit}
                  </span>
                ))}
              </div>
            </div>

            {/* Dynamic Price Section */}
            <div className="mb-4">{renderPriceSection(service)}</div>

            {/* Book Now Button */}
            <div style={{ marginTop: "auto" }}>
              <Button
                type="primary"
                block
                size="large"
                disabled={!service.is_available}
                className="w-full h-10 sm:h-12 text-sm sm:text-lg font-semibold bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600"
                onClick={() => onBookNow(service)}
              >
                {service.is_available ? "Book Now" : "Not Available"}
              </Button>
            </div>
          </div>
        </div>

        {/* All Dates Modal/Bottom Sheet */}
        {showAllDates && (
          <div className="fixed inset-0 z-50">
            {/* Desktop Modal */}
            <div className="hidden md:flex items-center justify-center min-h-screen p-4">
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleBackdropClick}
              ></div>

              {/* Modal Content */}
              <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full max-h-96 overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    🎉 {service.name}
                  </h3>
                  <button
                    onClick={handleCloseDates}
                    className="text-gray-400 hover:text-gray-600 text-xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                  >
                    ×
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-4">
                  <div className="mb-3">
                    <span className="text-amber-600 font-semibold text-sm">
                      📅 All Special Dates ({availableDates.length}):
                    </span>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {availableDates.map((date, idx) => (
                      <div
                        key={idx}
                        className="flex items-center p-2 bg-amber-50 rounded-lg border border-amber-200"
                      >
                        <span className="text-amber-800 font-medium">
                          {formatDateForDisplay(date)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Bottom Sheet */}
            <div className="md:hidden">
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleBackdropClick}
              ></div>

              {/* Bottom Sheet */}
              <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-xl shadow-2xl max-h-96 overflow-hidden animate-slide-up">
                {/* Handle Bar */}
                <div className="flex justify-center p-2">
                  <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
                </div>

                {/* Bottom Sheet Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    🎉 Special Dates
                  </h3>
                  <button
                    onClick={handleCloseDates}
                    className="text-gray-400 hover:text-gray-600 text-xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                  >
                    ×
                  </button>
                </div>

                {/* Bottom Sheet Body */}
                <div className="p-4">
                  <div className="mb-3">
                    <span className="text-amber-600 font-semibold text-sm">
                      📅 {service.name} ({availableDates.length} dates):
                    </span>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {availableDates.map((date, idx) => (
                      <div
                        key={idx}
                        className="flex items-center p-3 bg-amber-50 rounded-lg border border-amber-200"
                      >
                        <span className="text-amber-800 font-medium">
                          {formatDateForDisplay(date)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add CSS for animations and special effects */}
        <style jsx>{`
          .special-occasion-card {
            position: relative;
          }

          @media (min-width: 768px) {
            .special-occasion-card {
              box-shadow: 0 0 30px rgba(245, 158, 11, 0.4);
            }

            .special-occasion-card:hover {
              box-shadow: 0 0 40px rgba(245, 158, 11, 0.6);
            }
          }

          @keyframes gradient-x {
            0%,
            100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          .animate-gradient-x {
            background-size: 300% 300%;
            animation: gradient-x 3s ease infinite;
          }

          @keyframes slide-up {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }

          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </>
    );
  }
);

const ServicesGrid = () => {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [likedServices, setLikedServices] = useState(new Set());
  const [customPrices, setCustomPrices] = useState({});
  // Single API call to get all services grouped by type
  const [servicesResponse, requestServices] = useApi(
    "/user/services",
    null,
    API_METHODS.GET
  );

  // Load all services on mount
  useEffect(() => {
    requestServices();
    // eslint-disable-next-line no-undef
    fbq("track", "PageView");
  }, []);

  const getServiceTypeIcon = (icon) => icon || "🕉️";
  const getServiceIcon = (imageUrl, serviceTypeIcon) =>
    imageUrl ? imageUrl : serviceTypeIcon || "🕉️";

  const getAvailabilityText = (isAvailable) => {
    if (!isAvailable) return "Currently Unavailable";
    const options = ["Today 6:00 PM", "Tomorrow 8:00 AM", "Available Now", "Next Available"];
    return options[Math.floor(Math.random() * options.length)];
  };

  const handleCustomPriceChange = (serviceId, value) => {
    // Allow empty string for better UX
    if (value === "") {
      setCustomPrices((prev) => ({
        ...prev,
        [serviceId]: "",
      }));
      return;
    }

    // Remove any non-numeric characters except decimal point
    let numericValue = value.replace(/[^\d.]/g, "");

    // Ensure only one decimal point
    const parts = numericValue.split(".");
    if (parts.length > 2) {
      numericValue = parts[0] + "." + parts.slice(1).join("");
    }

    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      numericValue = parts[0] + "." + parts[1].substring(0, 2);
    }

    setCustomPrices((prev) => ({
      ...prev,
      [serviceId]: numericValue,
    }));
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const renderPriceSection = (service) => {
    switch (service.price_type) {
      case "fixed":
        return (
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-orange-600">
              {formatCurrency(service.price)}
            </span>
          </div>
        );

      case "custom":
        return (
          <div className="space-y-2">
            <div className="text-lg font-semibold text-orange-600">Any Amount</div>
            <input
              type="text"
              placeholder="Enter amount"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={customPrices[service.id] || ""}
              onChange={(e) => handleCustomPriceChange(service.id, e.target.value)}
              autoComplete="off"
              inputMode="numeric"
            />
            <div className="text-xs text-gray-500">
              Min: ₹10 {service.max_price && `• Max: ${formatCurrency(service.max_price)}`}
            </div>
          </div>
        );

      case "minimum":
        return (
          <div className="space-y-2">
            <div className="text-lg font-semibold text-orange-600">
              Min {formatCurrency(service.min_price)}
            </div>
            <input
              type="text"
              placeholder={`Min ₹${service.min_price}`}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={customPrices[service.id] || ""}
              onChange={(e) => handleCustomPriceChange(service.id, e.target.value)}
              autoComplete="off"
              inputMode="numeric"
            />
            <div className="text-xs text-gray-500">
              Min: {formatCurrency(service.min_price)}{" "}
              {service.max_price && `• Max: ${formatCurrency(service.max_price)}`}
            </div>
          </div>
        );

      default:
        return <div className="text-lg font-semibold text-gray-600">Contact for Price</div>;
    }
  };

  // Transform services data - must be declared before the useEffect that depends on it
  const allServiceTypes = useMemo(() => {
    return (
      servicesResponse?.data?.map((serviceType) => ({
        ...serviceType,
        services: serviceType.services.map((service) => ({
          id: service.id,
          slug: service.slug,
          name: service.name,
          duration: `${service.duration_minutes} mins`,
          price: service.price,
          price_type: service.price_type,
          min_price: service.min_price,
          max_price: service.max_price,
          description: service.description,
          image_url: service.image_url,
          service_type_id: serviceType.service_type_id,
          service_type_name: serviceType.service_type_name,
          service_type_icon: serviceType.service_type_icon,
          popular: true,
          rating: 4.5 + Math.random() * 0.4,
          reviews: Math.floor(Math.random() * 2000) + 500,
          nextAvailable: getAvailabilityText(service.is_available),
          benefits: [
            "Traditional Ritual","Divine Blessings", "Sacred Service",
          ],
          is_available: service.is_available,
          is_date_restricted: service.is_date_restricted,
          available_dates: service.available_dates || [],
        })),
      })) || []
    );
  }, [servicesResponse?.data, getAvailabilityText]);

  const startBooking = (service) => {
    const isCustomPricing = service.price_type === "custom" || service.price_type === "minimum";
    if (isCustomPricing) {
      const amount = parseFloat(customPrices[service.id]);
      if (!amount || amount <= 0) {
        message.error("Please enter an amount");
        return;
      }
      if (service.price_type === "minimum" && service.min_price && amount < service.min_price) {
        message.error(`Minimum amount is ₹${service.min_price}`);
        return;
      }
      if (service.max_price && amount > service.max_price) {
        message.error(`Maximum amount is ₹${service.max_price}`);
        return;
      }
      navigate(`/book/${service.slug}?amount=${amount}`);
      return;
    }
    navigate(`/book/${service.slug}`);
  };


  // Generate filter buttons from service types - memoized
  const filterButtons = useMemo(
    () => [
      { id: "all", name: "All Services", icon: "🕉️" },
      ...allServiceTypes
        .filter((type) => type.services.length > 0) // Only include service types with services
        .map((type) => ({
          id: type.service_type_id,
          name: type.service_type_name,
          icon: getServiceTypeIcon(type.service_type_icon),
        })),
    ],
    [allServiceTypes, getServiceTypeIcon]
  );

  // Frontend filtering - memoized
  const filteredServiceTypes = useMemo(() => {
    return selectedFilter === "all"
      ? allServiceTypes.filter((type) => type.services.length > 0) // Only show service types with services
      : allServiceTypes.filter(
          (type) =>
            type.service_type_id === parseInt(selectedFilter) &&
            type.services.length > 0
        );
  }, [selectedFilter, allServiceTypes]);

  const toggleLike = useCallback(
    (serviceId) => {
      const newLiked = new Set(likedServices);
      if (newLiked.has(serviceId)) {
        newLiked.delete(serviceId);
      } else {
        newLiked.add(serviceId);
      }
      setLikedServices(newLiked);
    },
    [likedServices]
  );

  // Handle loading states
  if (!isEmpty(servicesResponse) && servicesResponse.isLoading) {
    return (
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              🪔 Mahalaxmi Ambabai Temple Services
            </h2>
            <div className="flex justify-center items-center py-20">
              <Spin size="large" />
              <span className="ml-4 text-lg text-gray-600">
                Loading temple services...
              </span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (isEmpty(servicesResponse)) {
    return <ServerDownResult />;
  }

  return (
    <>
      <HeaderComponent />
      <div className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              🪔 Mahalaxmi Ambabai Temple Services
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Authentic rituals • Blessed prasad • Personal dedication by name •
              No third party involved
            </p>
            <div className="hidden md:block">
              <WhatsAppCommunityPill />
            </div>
            <div className="md:hidden mb-6">
              <WhatsAppCommunityCard />
            </div>
          </div>

          {/* Enhanced Tab-Style Filter Buttons - Hidden on Mobile */}
          <div className="relative mb-12 hidden md:block">
            <div className="flex flex-wrap justify-center border-b border-gray-200">
              {filterButtons.map((filter, index) => (
                <button
                  key={filter.id}
                  onClick={() => setSelectedFilter(filter.id)}
                  className={`relative px-6 py-4 font-medium transition-all duration-300 flex items-center space-x-2 hover:text-orange-600 ${
                    selectedFilter === filter.id
                      ? "text-orange-600"
                      : "text-gray-600"
                  }`}
                >
                  <span className="text-lg">{filter.icon}</span>
                  <span className="font-semibold">{filter.name}</span>

                  {/* Active tab indicator */}
                  {selectedFilter === filter.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500"></div>
                  )}

                  {/* Hover effect */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-300 scale-x-0 hover:scale-x-100 transition-transform duration-300 origin-center"></div>
                </button>
              ))}
            </div>
          </div>

          {/* Services Grouped by Type */}
          <div className="space-y-16">
            {filteredServiceTypes.map((serviceType) => (
              <div key={serviceType.service_type_id}>
                {/* Enhanced Card-Section Header - Responsive */}
                <div className="relative mb-10">
                  {/* Background card with gradient */}
                  <div className="bg-gradient-to-r from-orange-50 via-white to-red-50 rounded-2xl border-2 border-orange-200 shadow-lg p-4 md:p-6 transform hover:scale-[1.02] transition-all duration-300">
                    {/* Main header content */}
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      <div className="flex items-center space-x-3 md:space-x-4">
                        {/* Large service type icon with animation */}
                        <div className="relative">
                          <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center text-white text-xl md:text-2xl shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300">
                            {getServiceTypeIcon(serviceType.service_type_icon)}
                          </div>
                          {/* Glow effect */}
                          <div className="absolute inset-0 w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-orange-400 to-red-400 rounded-2xl blur-xl opacity-30 -z-10"></div>
                        </div>

                        {/* Service type details */}
                        <div className="text-center sm:text-left">
                          <h3 className="text-xl md:text-3xl font-bold text-gray-800 mb-1 tracking-wide">
                            {serviceType.service_type_name}
                          </h3>
                          {serviceType.service_type_description && (
                            <p className="text-gray-600 text-xs md:text-sm italic hidden sm:block">
                              {serviceType.service_type_description}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Decorative arrow - Hidden on mobile */}
                      <div className="hidden lg:block text-orange-400 animate-bounce">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M7 14l5-5 5 5z" />
                        </svg>
                      </div>
                    </div>

                    {/* Bottom accent line */}
                    <div className="mt-4 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 rounded-full opacity-60"></div>
                  </div>

                  {/* Floating decorative elements */}
                  <div className="absolute -top-2 -left-2 w-4 h-4 bg-orange-300 rounded-full opacity-60 animate-ping"></div>
                  <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-red-300 rounded-full opacity-40"></div>
                </div>

                {/* Services Grid for this Type */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {serviceType.services.map((service) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      customPrices={customPrices}
                      onCustomPriceChange={handleCustomPriceChange}
                      likedServices={likedServices}
                      toggleLike={toggleLike}
                      formatCurrency={formatCurrency}
                      getServiceIcon={getServiceIcon}
                      renderPriceSection={renderPriceSection}
                      onBookNow={startBooking}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* No Services Message */}
          {filteredServiceTypes.length === 0 && !servicesResponse.isLoading && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🏛️</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                No services available
              </h3>
              <p className="text-gray-600">
                {selectedFilter === "all"
                  ? "Temple services are currently being updated. Please check back later."
                  : "No services available in this category. Try selecting a different filter."}
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ServicesGrid;
